import { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useMeta } from "../hooks";
import { useAuth } from "../store/auth";
import Icon from "./Icon";

const BUCKETS = [["to", "Para"], ["cc", "CC"], ["bcc", "CCO"]];

// Recipient selector that mixes system users + the email directory (contacts),
// lets you search across all their fields, and assign each to Para / CC / CCO.
export default function RecipientPicker({ onChange }) {
  const { users } = useMeta();
  // Marcas apagadas en Configuración → Preferencias de la plataforma (empresa.usa_marca)
  const { features } = useAuth();
  const usaMarca = features?.usa_marca !== false;
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({ to: [], cc: [], bcc: [] }); // arrays of {email,label}

  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    onChange?.({ to: sel.to.map((x) => x.email), cc: sel.cc.map((x) => x.email), bcc: sel.bcc.map((x) => x.email) });
  }, [sel]); // eslint-disable-line

  const candidates = useMemo(() => {
    const us = users.map((u) => ({
      email: u.email, label: u.full_name, sub: "Usuario del sistema",
      hay: `${u.full_name} ${u.email} usuario`,
    }));
    const cs = contacts.map((c) => ({
      email: c.email, label: c.name,
      sub: [c.contact_type, c.company, usaMarca && c.brand, c.store, c.country].filter(Boolean).join(" · "),
      hay: `${c.name} ${c.email} ${c.company} ${c.country} ${usaMarca ? c.brand : ""} ${c.store} ${c.contact_type} ${(c.tags || []).join(" ")} ${c.phone}`,
    }));
    const all = [...us, ...cs];
    if (!q.trim()) return all.slice(0, 60);
    const t = q.toLowerCase();
    return all.filter((x) => x.hay.toLowerCase().includes(t)).slice(0, 60);
  }, [users, contacts, q, usaMarca]);

  const inAny = (email) => ["to", "cc", "bcc"].some((b) => sel[b].some((x) => x.email === email));
  const add = (bucket, item) => setSel((s) => (inAny(item.email) ? s : { ...s, [bucket]: [...s[bucket], { email: item.email, label: item.label }] }));
  const remove = (bucket, email) => setSel((s) => ({ ...s, [bucket]: s[bucket].filter((x) => x.email !== email) }));
  const addFree = (bucket) => {
    const e = q.trim();
    if (e && e.includes("@") && !inAny(e)) { setSel((s) => ({ ...s, [bucket]: [...s[bucket], { email: e, label: e }] })); setQ(""); }
  };

  const total = sel.to.length + sel.cc.length + sel.bcc.length;

  return (
    <div className="space-y-2">
      {/* Selected buckets */}
      {BUCKETS.map(([b, lbl]) => (
        sel[b].length > 0 && (
          <div key={b} className="flex flex-wrap items-center gap-1">
            <span className="w-10 shrink-0 text-xs font-semibold text-slate-500">{lbl}:</span>
            {sel[b].map((x) => (
              <span key={x.email} className="chip bg-brand-50 text-brand-700" title={x.email}>
                {x.label}
                <button onClick={() => remove(b, x.email)} className="ml-1 text-brand-400 hover:text-rose-500"><Icon name="x" size={12} /></button>
              </span>
            ))}
          </div>
        )
      ))}

      <div className="relative">
        <Icon name="search" size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
        <input className="input pl-8" placeholder="Buscar usuario o contacto (empresa, marca, país, tienda, etiqueta, tipo…)"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {q.trim() && q.includes("@") && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Agregar «{q.trim()}» como:
          {BUCKETS.map(([b, lbl]) => <button key={b} className="btn-ghost px-2 py-0.5 text-xs" onClick={() => addFree(b)}>{lbl}</button>)}
        </div>
      )}

      <div className="max-h-52 divide-y divide-slate-50 overflow-y-auto rounded-lg border border-slate-200">
        {candidates.map((c) => (
          <div key={`${c.label}-${c.email}`} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{c.label} <span className="text-xs font-normal text-slate-400">{c.email}</span></div>
              {c.sub && <div className="truncate text-[11px] text-slate-400">{c.sub}</div>}
            </div>
            {inAny(c.email) ? (
              <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600"><Icon name="check" size={11} /> agregado</span>
            ) : (
              <div className="flex shrink-0 gap-1">
                {BUCKETS.map(([b, lbl]) => (
                  <button key={b} onClick={() => add(b, c)} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-brand-50 hover:text-brand-700">{lbl}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        {!candidates.length && <p className="px-2 py-4 text-center text-xs text-slate-400">Sin coincidencias</p>}
      </div>
      <p className="text-[11px] text-slate-400">{total} destinatario(s). Usa Para / CC / CCO (copia oculta).</p>
    </div>
  );
}
